import { useState } from 'react';
import { Check, Gift, Sparkles, Globe, ArrowRight, Rocket, Layers, Crown, Flag, Eye, ChevronUp, ChevronDown, CreditCard, ExternalLink, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { ShareWhatsApp } from './ShareWhatsApp';

const PROVIDER_STYLES = {
  stripe: { bg: '#635BFF', label: 'Tarjeta / Stripe' },
  hotmart: { bg: '#EF4E23', label: 'Hotmart' },
  niubiz: { bg: '#F97316', label: 'Niubiz' },
};

const ProviderLogo = ({ provider }) => {
  const key = provider.toLowerCase();
  if (key === 'stripe') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <img src="/paylogos/stripe.svg" alt="Stripe" className="h-5 w-auto" loading="lazy" />
        <span className="text-[10px] font-semibold text-white/70">Tarjeta</span>
      </span>
    );
  }
  if (key === 'hotmart') {
    return (
      <span className="inline-flex items-center gap-2 text-white">
        <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-white">
          <img src="/paylogos/hotmart.svg" alt="Hotmart" className="h-4 w-auto object-contain" loading="lazy" />
        </span>
        <span className="text-[15px] font-black tracking-tight">Hotmart</span>
      </span>
    );
  }
  if (key === 'niubiz') {
    return (
      <span className="inline-flex items-center gap-2 text-white">
        <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-white text-[16px] font-black leading-none" style={{ color: '#6E2B9F' }}>N</span>
        <span className="text-[15px] font-black tracking-tight lowercase">niubiz</span>
      </span>
    );
  }
  return <span className="text-sm font-bold text-white">{provider}</span>;
};

const PayBubble = ({ payLinks, accent, plan, children }) => {
  const [open, setOpen] = useState(true);
  if (!payLinks || payLinks.length === 0) return children;
  return (
    <div
      className="group"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`pay-trigger-${plan}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left rounded-2xl cursor-pointer transition-all"
        style={{ boxShadow: open ? `0 0 0 2px ${accent}` : 'none' }}
      >
        {children}
        <span className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color: accent }}>
          <CreditCard className="h-3.5 w-3.5" />
          {open ? 'Elige tu forma de pago' : 'Ver formas de pago'}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="mt-2 cv-reveal" data-testid={`pay-bubble-${plan}`}>
          <div className="rounded-2xl border border-white/15 bg-[#0d1526] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] p-2 space-y-1.5">
            <p className="px-1.5 pt-1 pb-0.5 text-[10px] font-black uppercase tracking-[1.5px] text-slate-500">Paga con</p>
            {payLinks.map((l) => {
              const key = l.provider.toLowerCase();
              const st = PROVIDER_STYLES[key] || { bg: accent, label: l.provider };
              return (
                <a
                  key={l.provider}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pay-link-${plan}-${key}`}
                  className="flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 transition-colors hover:brightness-110"
                  style={{ backgroundColor: st.bg }}
                >
                  <ProviderLogo provider={l.provider} />
                  <ExternalLink className="h-4 w-4 text-white/80" />
                </a>
              );
            })}
            <div className="flex items-center justify-center gap-1.5 pt-1 pb-0.5 text-[10px] font-semibold text-slate-400" data-testid={`secure-badge-${plan}`}>
              <Lock className="h-3 w-3 text-[#80C753]" />
              Pago 100% seguro
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const THEMES = {
  cero: {
    accent: '#45A0EE',
    grad: 'from-[#45A0EE] to-[#6FC0FF]',
    glow: 'rgba(69,160,238,0.4)',
    cardBg: '#12335a',
    icon: Flag,
    vibe: 'Agente',
  },
  viajero: {
    accent: '#2DD4BF',
    grad: 'from-[#14B8A6] to-[#2DD4BF]',
    glow: 'rgba(45,212,191,0.4)',
    cardBg: '#0f3b38',
    icon: Globe,
    vibe: 'Uso personal',
  },
  starter: {
    accent: '#45A0EE',
    grad: 'from-[#45A0EE] to-[#6FC0FF]',
    glow: 'rgba(69,160,238,0.45)',
    cardBg: '#12335a',
    icon: Rocket,
    vibe: 'Despega',
  },
  duo: {
    accent: '#80C753',
    grad: 'from-[#80C753] to-[#A6E06B]',
    glow: 'rgba(128,199,83,0.45)',
    cardBg: '#1c4a22',
    icon: Layers,
    vibe: 'Combina',
  },
  pro: {
    accent: '#B79CFF',
    grad: 'from-[#8B5CF6] via-[#9d7bf5] to-[#B79CFF]',
    glow: 'rgba(167,139,250,0.5)',
    cardBg: '#332a63',
    icon: Crown,
    vibe: 'Acelera',
  },
  empire: {
    accent: '#E3C766',
    grad: 'from-[#C9A84C] via-[#E3C766] to-[#C9A84C]',
    glow: 'rgba(201,168,76,0.5)',
    cardBg: '#463714',
    icon: Crown,
    vibe: 'Imperio',
  },
};

const PricingOption = ({
  name,
  description,
  price,
  originalPrice,
  features,
  bonuses,
  courierBonus,
  isRecommended,
  isPopular,
  onSelect,
  plan,
  monthlyFrom,
  oneTime,
  upgradeNote,
  paymentPlanText,
  bono,
  promo,
  tipo,
  agentNote,
  customPrice,
  payLinks,
  promoPrice,
  promoBadge,
  promoExtra,
  promoBonus,
}) => {
  const theme = THEMES[plan] || THEMES.starter;
  const Icon = theme.icon;
  const highlighted = isPopular || isRecommended;
  const [showValue, setShowValue] = useState(false);

  return (
    <div
      data-testid={`pricing-card-${plan}`}
      className="group relative cv-glass rounded-3xl flex flex-col h-full overflow-hidden transition-all duration-300 hover:-translate-y-2"
      style={{
        backgroundColor: theme.cardBg,
        border: `1.5px solid ${theme.accent}${plan === 'empire' ? '' : '55'}`,
        boxShadow: highlighted || plan === 'empire' ? `0 25px 60px -15px ${theme.glow}` : undefined,
      }}
    >
      {/* Glow superior animado en la tarjeta destacada */}
      {highlighted && (
        <div
          className="absolute -top-px left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }}
        />
      )}
      {/* Banda superior de color */}
      <div className={`h-1.5 w-full bg-gradient-to-r ${theme.grad}`} />

      {promoBadge && (
        <div
          className="absolute top-0 left-0 right-0 z-20 py-1.5 text-center text-white text-[11px] font-black uppercase tracking-wider"
          style={{ background: 'linear-gradient(100deg,#E23744,#ff5a67)' }}
          data-testid={`promo-ribbon-${plan}`}
        >
          🔥 {promoBadge}
        </div>
      )}

      {highlighted && (
        <div
          className="absolute right-4 px-3 py-1 text-white text-[10px] font-black uppercase tracking-wider rounded-full shadow-lg flex items-center gap-1 z-10"
          style={{ backgroundColor: theme.accent, boxShadow: `0 0 20px ${theme.glow}`, top: promoBadge ? '2.5rem' : '1.25rem' }}
        >
          <Sparkles className="h-3 w-3" />
          {isPopular ? 'Más elegido' : 'Recomendado'}
        </div>
      )}

      <div className={`p-6 flex flex-col flex-1 ${promoBadge ? 'pt-9' : ''}`}>
        {/* Icono + vibe */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${theme.grad} cv-float`}
            style={{ boxShadow: `0 8px 24px ${theme.glow}` }}
          >
            <Icon className="h-6 w-6 text-white" />
          </div>
          <span className="text-[11px] font-black uppercase tracking-[2px]" style={{ color: theme.accent }}>{theme.vibe}</span>
        </div>

        {tipo && (
          <span
            className="block text-[11px] font-bold uppercase tracking-[3px] mb-1.5"
            style={{ color: theme.accent }}
            data-testid={`pricing-tipo-${plan}`}
          >
            {tipo}
          </span>
        )}
        <h4 className="text-2xl font-black text-white mb-1 min-h-[3rem] leading-tight">{name}</h4>
        <p className="text-sm text-slate-400 mb-5 min-h-[2.5rem]">{description}</p>

        <ul className="space-y-2.5 mb-6">
          {features.map((feature, idx) => {
            const isObj = typeof feature === 'object' && feature !== null;
            const label = isObj ? feature.label : feature;
            const sub = isObj ? feature.sub : null;
            return (
              <li key={idx} className="text-sm">
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.accent}22` }}>
                    <Check className="h-3 w-3" style={{ color: theme.accent }} />
                  </span>
                  <span className={isObj ? 'text-white font-semibold' : 'text-slate-300'}>{label}</span>
                </div>
                {sub && sub.length > 0 && (
                  <ul className="mt-1.5 ml-7 space-y-1">
                    {sub.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-xs text-slate-400">
                        <span className="mt-[6px] flex-shrink-0 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.accent }} />
                        <span className="leading-snug">{s}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {/* Bonuses */}
        {bonuses && bonuses.length > 0 && (
          <div className="bg-gradient-to-b from-[#80C753]/10 to-[#45A0EE]/5 border border-[#80C753]/30 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="h-5 w-5 text-[#80C753]" />
              <span className="text-sm font-bold text-[#80C753] uppercase tracking-wide">Bonos Incluidos</span>
            </div>
            <div className="space-y-2">
              {bonuses.map((b) => (
                <div key={b.title} className="flex items-start gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-semibold text-white">{b.title}</span>
                    {b.value && <span className="text-xs text-[#80C753] font-bold ml-1">({b.value})</span>}
                    {b.desc && <p className="text-[10px] text-slate-400 mt-0.5">{b.desc}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Courier Bonus (Empire only) */}
        {courierBonus && (
          <div className="bg-black/30 border border-white/10 rounded-xl p-4 mb-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-5 w-5 text-[#45A0EE]" />
              <span className="text-xs font-bold text-[#45A0EE] uppercase tracking-wide">Negocio Adicional Exclusivo</span>
            </div>
            <h5 className="text-sm font-bold mb-1">{courierBonus.title}</h5>
            <p className="text-[11px] text-slate-300 leading-relaxed mb-2">{courierBonus.description}</p>
            <div className="space-y-1.5">
              {courierBonus.perks.map((perk) => (
                <div key={perk} className="flex items-start gap-1.5">
                  <Check className="h-3 w-3 text-[#80C753] flex-shrink-0 mt-0.5" />
                  <span className="text-[11px] text-slate-200">{perk}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-auto pt-2">
          {promoBonus && (
            <div
              className="relative mb-4 rounded-2xl p-[1.5px] overflow-hidden"
              style={{ background: 'linear-gradient(135deg,#E3C766,#fff2c2,#E3C766)' }}
              data-testid={`promo-bonus-${plan}`}
            >
              <div className="relative rounded-2xl bg-gradient-to-br from-[#120f06] via-[#1a1508] to-[#0f1320] p-4 overflow-hidden">
                <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#E3C766]/20 blur-2xl pointer-events-none" />
                <div className="relative flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#E3C766] to-[#caa23a]">
                    <Rocket className="h-4 w-4 text-[#120f06]" />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#E3C766]">Bonus de hoy · Incluido gratis</span>
                </div>
                <p className="relative text-lg font-black text-white leading-tight">El despegue de tu agencia 🚀</p>
                <p className="relative text-[13px] text-amber-100/90 font-semibold leading-snug mt-1">
                  <span className="text-white font-black">Training de Ventas de 30 días</span> junto al equipo ganador de Club Viajemos.
                </p>
                <p className="relative text-xs text-[#E3C766] font-black italic mt-1.5">No hay mejor comienzo.</p>
              </div>
            </div>
          )}
          {upgradeNote && (
            <div
              className="mb-3 rounded-xl border p-3 flex items-start gap-2"
              style={{ borderColor: '#45A0EE66', backgroundColor: '#45A0EE14' }}
              data-testid={`pricing-upgrade-note-${plan}`}
            >
              <ArrowRight className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#45A0EE]" />
              <p className="text-xs text-slate-200 leading-snug">{upgradeNote}</p>
            </div>
          )}

          {agentNote ? (
            <>
              {monthlyFrom && (
                <PayBubble payLinks={payLinks} accent={theme.accent} plan={plan}>
                <div className="rounded-2xl p-4 mb-4 bg-white/[0.04] border border-white/10">
                  <p className="text-3xl font-black text-white leading-none whitespace-nowrap" data-testid={`total-price-${plan}`}>
                    ${price?.toLocaleString("en-US")} <span className="text-sm font-semibold text-slate-400">USD</span>
                  </p>
                  <p className="text-[11px] text-slate-400 mt-2 leading-snug">
                    O puedes pagarlo hasta en <span className="text-white font-bold">12 meses</span> con nuestros socios: Klarna, Afterpay, Hotmart y Mercado Pago.
                  </p>
                </div>
                </PayBubble>
              )}
              <div
                className="rounded-xl p-4 mb-4 border"
                style={{ borderColor: `${theme.accent}66`, backgroundColor: `${theme.accent}14` }}
                data-testid={`agent-note-${plan}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Flag className="h-4 w-4 flex-shrink-0" style={{ color: theme.accent }} />
                  <span className="text-xs font-black uppercase tracking-wide" style={{ color: theme.accent }}>Agente patrocinado</span>
                </div>
                <p className="text-xs text-slate-200 leading-relaxed">{agentNote}</p>
              </div>
              <div
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-xs font-bold uppercase tracking-wide text-white"
                style={{ background: `${theme.accent}22`, border: `1px solid ${theme.accent}55` }}
                data-testid={`agent-badge-${plan}`}
              >
                <Check className="h-4 w-4" style={{ color: theme.accent }} />
                Acceso otorgado por tu agencia
              </div>
              <ShareWhatsApp name={name} price={price} payLinks={payLinks} plan={plan} />
            </>
          ) : (
            <>
              {/* Valor + formas de pago (SIEMPRE visible, sin ocultar) */}
              <div className="cv-reveal">
              {/* Precio */}
              <PayBubble payLinks={payLinks} accent={theme.accent} plan={plan}>
              <div className="rounded-2xl p-4 mb-4 bg-white/[0.04] border border-white/10">
                {promoPrice ? (
                  <>
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                      <span className="text-slate-400 line-through text-xl font-semibold">${price.toLocaleString("en-US")}</span>
                      <span className="text-3xl font-black leading-none whitespace-nowrap" style={{ color: '#80C753' }} data-testid={`total-price-${plan}`}>
                        ${promoPrice.toLocaleString("en-US")} <span className="text-sm font-semibold text-slate-400">USD</span>
                      </span>
                    </div>
                    <p className="text-[11px] font-black uppercase tracking-wide mt-1" style={{ color: '#E23744' }}>Precio SOLO HOY</p>
                  </>
                ) : (
                  <p className="text-3xl font-black text-white leading-none whitespace-nowrap" data-testid={`total-price-${plan}`}>
                    ${price.toLocaleString("en-US")} <span className="text-sm font-semibold text-slate-400">USD</span>
                  </p>
                )}
                <p className="text-[11px] text-slate-400 mt-2 leading-snug">
                  O puedes pagarlo hasta en <span className="text-white font-bold">24 meses</span> con nuestros socios: Klarna, Afterpay, Hotmart y Mercado Pago.
                </p>
              </div>
              </PayBubble>

              {promoExtra && (
                <div
                  className="rounded-xl p-3 mb-4 border flex items-start gap-2"
                  style={{ borderColor: '#80C75366', backgroundColor: '#80C75314' }}
                  data-testid={`promo-extra-${plan}`}
                >
                  <Gift className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#80C753]" />
                  <p className="text-xs font-bold text-[#cdeab0] leading-snug">{promoExtra}</p>
                </div>
              )}

              <ShareWhatsApp name={name} price={price} payLinks={payLinks} plan={plan} />

              {/* Bono de Contado 72 horas */}
              {bono && (
                <div
                  className="rounded-xl p-4 mb-4 border"
                  style={{ borderColor: `${theme.accent}66`, backgroundColor: `${theme.accent}14` }}
                  data-testid={`bono-contado-${plan}`}
                >
                  <div className="flex items-center gap-2 mb-2.5">
                    <Gift className="h-4 w-4 flex-shrink-0" style={{ color: theme.accent }} />
                    <span className="text-xs font-black uppercase tracking-wide" style={{ color: theme.accent }}>Bono de Contado 72 horas</span>
                  </div>
                  {bono.special ? (
                    <>
                      <div className="flex items-baseline gap-2.5 flex-wrap mb-1">
                        <span className="text-slate-400 line-through text-lg font-semibold">${bono.strike.toLocaleString("en-US")}</span>
                        <span className="text-3xl font-black leading-none" style={{ color: theme.accent }}>${bono.price.toLocaleString("en-US")}</span>
                      </div>
                      {bono.savings && (
                        <p className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: theme.accent }}>Ahorras ${bono.savings.toLocaleString("en-US")}</p>
                      )}
                      <p className="text-xs text-slate-200 leading-relaxed">{bono.text}</p>
                    </>
                  ) : (
                    <p className="text-xs text-slate-200 leading-relaxed">{bono.text}</p>
                  )}
                </div>
              )}

              {/* Promo especial (sin título Bono de Contado) */}
              {promo && (
                <div
                  className="rounded-xl p-4 mb-4 border"
                  style={{ borderColor: `${theme.accent}66`, backgroundColor: `${theme.accent}14` }}
                  data-testid={`promo-especial-${plan}`}
                >
                  <div className="flex items-center gap-2 mb-2.5">
                    <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: theme.accent }} />
                    <span className="text-xs font-black uppercase tracking-wide" style={{ color: theme.accent }}>Promoción especial</span>
                  </div>
                  <p className="text-xs text-slate-200 leading-relaxed">{promo}</p>
                </div>
              )}

            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingOption;
